import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAgendamentoStepIndicator } from "../../../hooks/useAgendamentoStepIndicator";
import { useCadastrarAgendamento } from "../../../hooks/queries/agendamento/useCadastrarAgendamento/useCadastrarAgendamento";
import { useVincularAgendamentoCliente } from "../../../hooks/queries/agendamento/useVincularAgendamentoCliente/useVincularAgendamentoCliente";
import { useContextSite } from "../../../context/Context";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { useBrowserSessionStorage } from "../../../hooks/useBrowserSessionStorage";
import { Tracking } from "../../../services/Tracking";
import { TipoClienteEnum } from "../../../enums/tipoCliente";
import { IFormDeliveryResidenceV2Values } from "../../Molecules/FormDeliveryResidenceV2/useFormDeliveryResidenceV2";
import { IFormDeliveryConcessionaireV2Values } from "../../Molecules/FormDeliveryConcessionaireV2/useFormDeliveryConcessionaireV2";

export type IDeliveryV2Modalidade = "residencia" | "concessionaria";

export const useDeliveryV2 = () => {
  const navigate = useNavigate();
  const stepIndicator = useAgendamentoStepIndicator();
  const [modalidade, setModalidade] = useState<IDeliveryV2Modalidade>("residencia");
  const [isConcessionariaModalOpen, setIsConcessionariaModalOpen] =
    useState(false);
  const [uuidAgendamentoConcessionaria, setUuidAgendamentoConcessionaria] =
    useState<string>();
  const { setIsLoad } = useContextSite();
  const { userStorageData, isUserType } = useUserPermissions();
  const [uuidClienteAgendamento, , removeUuidClienteAgendamento] =
    useBrowserSessionStorage("@uuidClienteAgendamento");

  const { mutate: cadastrarAgendamento } = useCadastrarAgendamento();
  const { mutate: vincularAgendamentoCliente } =
    useVincularAgendamentoCliente();

  function handleModalidadeChange(value: IDeliveryV2Modalidade) {
    setModalidade(value);
    Tracking.userAction(
      value === "residencia"
        ? "mapa:agendamento/domicilio:clique-toggle-em-casa"
        : "mapa:agendamento/domicilio:clique-toggle-concessionaria",
    );
  }

  function vincularEAvancar(uuidAgendamento: string) {
    const isDespachante = isUserType(TipoClienteEnum.DESPACHANTE);
    const uuidCliente = isDespachante
      ? uuidClienteAgendamento
      : userStorageData?.uuidCliente;

    vincularAgendamentoCliente(
      { uuid: uuidAgendamento, uuidCliente },
      {
        onSuccess: () => {
          removeUuidClienteAgendamento();
          navigate(`/agendamento/${uuidAgendamento}/servicos`);
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error("mapa:agendamento/domicilio:vincular-cliente-falhou", {
            mensagem,
          });
        },
        onSettled: () => setIsLoad(false),
      },
    );
  }

  function submitResidencia(data: IFormDeliveryResidenceV2Values) {
    setIsLoad(true);
    cadastrarAgendamento(
      {
        concessionaria: false,
        atendimentoDomiciliar: {
          nome: data.nome,
          telefone: data.telefone,
          endereco: data.endereco,
        },
      },
      {
        onSuccess: ({ data: agendamento }) => vincularEAvancar(agendamento.uuid),
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error("mapa:agendamento/domicilio:criar-agendamento-falhou", {
            mensagem,
          });
          setIsLoad(false);
        },
      },
    );
  }

  function submitConcessionaria(data: IFormDeliveryConcessionaireV2Values) {
    setIsLoad(true);
    cadastrarAgendamento(
      {
        concessionaria: true,
        atendimentoConcessionaria: {
          nome: data.nome,
          telefone: data.telefone,
          uuidConcessionaria: data.uuidConcessionaria,
        },
      },
      {
        onSuccess: ({ data: agendamento }) => {
          setUuidAgendamentoConcessionaria(agendamento.uuid);
          setIsConcessionariaModalOpen(true);
          setIsLoad(false);
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error("mapa:agendamento/domicilio:criar-agendamento-falhou", {
            mensagem,
          });
          setIsLoad(false);
        },
      },
    );
  }

  function handleCancelarConcessionaria() {
    setIsConcessionariaModalOpen(false);
  }

  function handleConfirmarConcessionaria() {
    if (!uuidAgendamentoConcessionaria) return;

    Tracking.userAction(
      "mapa:agendamento/domicilio:clique-confirmar-concessionaria",
    );
    setIsConcessionariaModalOpen(false);
    setIsLoad(true);
    vincularEAvancar(uuidAgendamentoConcessionaria);
  }

  return {
    stepIndicator,
    modalidade,
    handleModalidadeChange,
    submitResidencia,
    submitConcessionaria,
    isConcessionariaModalOpen,
    handleCancelarConcessionaria,
    handleConfirmarConcessionaria,
  };
};
